import { FC, useMemo } from 'react';

import dayjs from 'dayjs';
import cn from 'classnames';
import { Avatar } from 'ui/components/img';
import { AvatarGroup } from 'ui/components/img/Avatar';
import { SIZE } from 'utils/constants';
import { useAppWrapperContext } from 'ui/layouts/AppWrapper';

import Button from '../Button';

type PROPS = {
  data: {
    notifyBefore: string;
    accountUserId: {
      accountId: string;
      createdAt: string;
      emailAddress: string;
      firstName: string;
      fullName: string;
      isEmailAddressVerified: boolean;
      isMfaEnabled: boolean;
      landlineNumber: string;
      lastName: string;
      password: string;
      passwordChangedAt: string;
      phoneNumber: string;
      photoUrl: string;
      recoveryMobileNumber: string;
      roleId: string;
      source: string;
      status: string;
      trashed: boolean;
      trashedAt: string;
      updatedAt: string;
    };
    guests: Array<{
      emailAddress: string;
      firstName: string;
      lastName: string;
    }>;
    eventUrl: string;
    eventType: string;
    tags: string[];
    startAt: string;
    endAt: string;
    _id: string;
    type: string;
    content?: string;
    title: string;
    actor?: any;
    userSource?: string;
  };
};

const EventEntry: FC<PROPS> = ({ data }) => {
  const eventDate = useMemo(() => {
    const { startAt, endAt } = data;
    const date = dayjs(startAt).format('MMM DD, YYYY');
    const startTime = dayjs(startAt).format('h:mm A');
    const endTime = dayjs(endAt).format('h:mm A');

    return `${date} | ${startTime} ${endTime}`;
  }, [data]);

  const {
    state: { isPreview },
  } = useAppWrapperContext();

  const join = useMemo(() => {
    if (data?.eventType !== 'VIDEO') return null;

    return (
      <Button
        intent={'link'}
        onClick={() => window.open(data.eventUrl, '_blank')}
        className="font-semibold text-info-base"
        label="Join"
      />
    );
  }, [data?.eventType, data.eventUrl]);

  const user = useMemo(() => {
    if (!isPreview) return data?.accountUserId;

    return JSON.parse(data?.actor?.meta);
  }, [isPreview, data]);

  return (
    <div className="py-[18px] text-secondary-dark space-y-[10px]">
      <div className="flex items-center">
        <div className="tracking-[0.04em] flex-1">
          <div className="text-sm font-semibold leading-[22px]">
            {data.title}
          </div>
          <div className="text-xs leading-5 ">{eventDate}</div>
        </div>
        {join}
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center w-full gap-[14px]">
          <Avatar
            size={SIZE.Medium}
            className={cn('flex-shrink-0')}
            user={user}
          />
          <div className="truncate">
            <div className={cn('text-xs leading-[22px] tracking-[0.04em]')}>
              Organizer
            </div>
            <div
              className={cn(
                'text-sm font-semibold leading-[22px] tracking-[0.04em]',
                'truncate',
              )}
            >
              {user?.firstName} {`${user?.lastName?.[0] ?? ''}.` || ''}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="whitespace-nowrap">Guest/s:</div>
          <AvatarGroup users={data.guests} size={SIZE.Small} />
        </div>
      </div>
    </div>
  );
};

export default EventEntry;
